import { useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/checkins";
import { DiaryDraftEditor } from "./DiaryDraftEditor";
import { PublishDialog } from "./PublishDialog";
import { errMsg } from "./ResolveDialog";

function Actions({ v, isLead, text, dirty, onSaved, onClose, onPublish }) {
  const call = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(ok); onSaved(r?.data); return true; } catch (e) { toast.error("Failed", { description: errMsg(e) }); return false; }
  };
  const status = (s, note) => call(() => api.post(`/checkin-templates/versions/${v.id}/status`, { status: s, note }), `Version ${s.replace("_", " ")}`);
  const save = (ok = "Draft saved") => {
    let content; try { content = JSON.parse(text); } catch { toast.error("Invalid JSON"); return Promise.resolve(false); }
    return call(() => api.put(`/checkin-templates/versions/${v.id}`, { content }), ok);
  };
  if (!isLead) return <p className="text-xs text-zinc-500">Only a clinical lead (Owner, Manager or Administration) can change templates.</p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {v.status === "draft" && <>
        <Button size="sm" data-testid="version-save-draft" disabled={!dirty} onClick={() => save()}>Save draft</Button>
        <Button size="sm" variant="outline" onClick={async () => { if (!dirty || await save(null)) status("clinical_review"); }} data-testid="version-to-review">
          {dirty ? "Save & submit for clinical review" : "Submit for clinical review"}
        </Button>
        {dirty && <span className="text-xs text-amber-700" data-testid="version-unsaved">Unsaved changes</span>}
      </>}
      {v.status === "clinical_review" && <>
        <Button size="sm" variant="outline" onClick={() => status("draft")} data-testid="version-back-to-draft">Send back to draft</Button>
        <Button size="sm" data-testid="version-publish" onClick={onPublish}>Approve & publish</Button>
      </>}
      {v.status === "published" && <>
        <Button size="sm" variant="outline" data-testid="version-new" onClick={() => call(() => api.post(`/checkin-templates/${v.template_id}/versions`, { from_version_id: v.id }), "New draft version created").then(onClose)}>New version from this</Button>
        <Button size="sm" variant="outline" data-testid="version-new-cohort" onClick={() => {
          const name = window.prompt("Name of the new cohort template (e.g. Balance & falls progress review)"); if (!name) return;
          const code = window.prompt("Cohort code (lowercase, e.g. balance_falls)"); if (!code) return;
          call(() => api.post("/checkin-templates", { name, cohort_code: code, from_version_id: v.id }), "New cohort template drafted").then(onClose);
        }}>Copy into new cohort template</Button>
        <Button size="sm" variant="destructive" data-testid="version-retire" onClick={() => window.confirm("Retire this version? Existing links keep working until they expire.") && status("retired")}>Retire</Button>
      </>}
    </div>
  );
}

function NameField({ v, parsed, editable, onChange }) {
  if (!parsed) return null;
  return (
    <div data-testid="version-name">
      <Label className="text-xs">Template name</Label>
      <Input className="mt-1" value={parsed.name || ""} readOnly={!editable} onChange={(e) => onChange({ ...parsed, name: e.target.value })} data-testid="version-name-input" />
      <p className="mt-1 text-2xs text-zinc-500" data-testid="version-name-hint">
        {editable ? "The template is renamed when this version is published." : v.status === "published" ? "To rename, create a new version from this one, change the name and publish it." : "Only drafts can be renamed."}
      </p>
    </div>
  );
}

function JsonBox({ v, isLead, text, setText }) {
  return (
    <>
      <p className="text-xs text-zinc-500">{v.status === "draft" ? "Edit questions, thresholds, safety rules and copy below. Published versions are immutable — historic responses keep the version they used." : "Read-only — published and retired versions never change."}</p>
      <Textarea className="font-mono text-xs" rows={22} value={text} readOnly={v.status !== "draft" || !isLead} onChange={(e) => setText(e.target.value)} data-testid="version-json" />
    </>
  );
}

export function VersionDialog({ vid, isLead, onClose, onChanged }) {
  const [v, setV] = useState(null);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [tab, setTab] = useState("form");
  const [publishing, setPublishing] = useState(false);
  const load = () => api.get(`/checkin-templates/versions/${vid}`).then((r) => {
    const t = JSON.stringify(r.data.content, null, 2);
    setV(r.data); setText(t); setSaved(t);
  });
  useEffect(() => { load(); }, [vid]); // eslint-disable-line react-hooks/exhaustive-deps
  const isDiary = v?.content?.kind === "diary";
  let parsed = null;
  try { parsed = JSON.parse(text); } catch { parsed = null; }
  const editable = v?.status === "draft" && isLead;
  return (
    <Dialog open onOpenChange={(o) => !o && (text === saved || window.confirm("Discard your unsaved changes?")) && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="checkin-version-dialog">
        <DialogHeader>
          <DialogTitle>{v ? `${v.content.name} — v${v.semantic_version}` : "Template version"}</DialogTitle>
          {v && <DialogDescription>Status: <strong data-testid="version-status">{v.status.replace("_", " ")}</strong>{v.published_at ? ` · published ${fmtDate(v.published_at)}` : ""}{v.review_due ? ` · evidence review due ${fmtDate(v.review_due)}` : ""}</DialogDescription>}
        </DialogHeader>
        {!v ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex-1 space-y-1">
                {v.approval_note && <p className="text-sm text-zinc-700">Approval: {v.approval_note}</p>}
                <p className="text-xs text-zinc-600">{v.evidence_note}</p>
              </div>
              {v.status !== "retired" && (
                <Button size="sm" variant="outline" onClick={() => window.open(`/progress-review/preview/${v.id}`, "_blank", "noopener")} data-testid="version-preview-btn"
                        title={text !== saved ? "Preview shows the last saved draft" : undefined}>
                  <Eye className="mr-1 h-3.5 w-3.5" /> Preview client pages
                </Button>
              )}
            </div>
            {v.lint?.length > 0 && <ul className="rounded bg-red-50 p-2 text-xs text-red-800" data-testid="version-lint">{v.lint.map((l) => <li key={l}>{l}</li>)}</ul>}
            <NameField v={v} parsed={parsed} editable={editable} onChange={(c) => setText(JSON.stringify(c, null, 2))} />
            {isDiary ? (
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList><TabsTrigger value="form" data-testid="version-tab-form">Questions, wording & flags</TabsTrigger><TabsTrigger value="json" data-testid="version-tab-json">Advanced (JSON)</TabsTrigger></TabsList>
                <TabsContent value="form" className="mt-3">
                  {parsed ? <DiaryDraftEditor content={parsed} readOnly={!editable} onChange={(c) => setText(JSON.stringify(c, null, 2))} />
                    : <p className="text-sm text-red-700" data-testid="version-json-invalid">The JSON has an error — fix it in the Advanced tab first.</p>}
                </TabsContent>
                <TabsContent value="json" className="mt-3 space-y-2"><JsonBox v={v} isLead={isLead} text={text} setText={setText} /></TabsContent>
              </Tabs>
            ) : <JsonBox v={v} isLead={isLead} text={text} setText={setText} />}
            <Actions v={v} isLead={isLead} text={text} dirty={text !== saved} onClose={onClose} onPublish={() => setPublishing(true)}
                     onSaved={() => { load(); onChanged(); }} />
          </div>
        )}
        {publishing && v && <PublishDialog v={v} onClose={() => setPublishing(false)} onDone={() => { setPublishing(false); load(); onChanged(); }} />}
      </DialogContent>
    </Dialog>
  );
}
